import React, { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Plus, Minus, Trash2, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import StaffLayout from '../../layouts/StaffLayout';
import { ordersApi, productsApi, customersApi } from '../../services/api';
import { formatCurrency, formatApiError, Spinner } from '../../utils/helpers';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';

const PARFUMS = ['Downy', 'Molto', 'Soklin', 'Harum', 'Komfort', 'So Klin', 'Custom'];
const DEMO_WA_SECURITY_MESSAGE = 'Untuk Keamanan, Akun DEMO tidak mengirimkan Pesan ke WhatsApp Langsung. Silahkan daftarkan Akun Toko Sendiri, untuk mencoba fitur WhatsApp ke Pelanggan.';

function ItemRow({ item, index, products, onUpdate, onRemove }) {
  const handleProductChange = (e) => {
    const p = products.find(p => p.id === e.target.value);
    onUpdate(index, { product_id: p?.id || '', product_name: p?.name || '', unit: p?.unit || 'kg', price: p?.price || 0 });
  };
  return (
    <div className="flex gap-3 items-start p-3 bg-slate-50 rounded-xl">
      <div className="flex-1 min-w-0">
        <select value={item.product_id} onChange={handleProductChange}
          className="w-full py-2 px-3 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-[#0f6c44] mb-2">
          <option value="">Pilih layanan...</option>
          {products.map(p => <option key={p.id} value={p.id}>{p.name} - {p.unit} - {formatCurrency(p.price)}</option>)}
        </select>
        <div className="flex gap-2">
          <div className="flex-1">
            <label className="text-xs text-slate-500 mb-1 block">Jumlah ({item.unit || 'kg'})</label>
            <div className="flex items-center gap-1">
              <button type="button" onClick={() => onUpdate(index, { quantity: Math.max(0.1, +(item.quantity - 0.5).toFixed(1)) })}
                className="w-8 h-8 rounded-lg bg-slate-200 hover:bg-slate-300 flex items-center justify-center transition-colors">
                <Minus size={14} />
              </button>
              <input type="number" step="0.1" min="0.1" value={item.quantity}
                onChange={e => onUpdate(index, { quantity: parseFloat(e.target.value) || 0 })}
                className="w-20 text-center py-1.5 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none" />
              <button type="button" onClick={() => onUpdate(index, { quantity: +(item.quantity + 0.5).toFixed(1) })}
                className="w-8 h-8 rounded-lg bg-slate-200 hover:bg-slate-300 flex items-center justify-center transition-colors">
                <Plus size={14} />
              </button>
            </div>
          </div>
          <div className="flex-1">
            <label className="text-xs text-slate-500 mb-1 block">Harga/unit</label>
            <input type="number" value={item.price} onChange={e => onUpdate(index, { price: parseFloat(e.target.value) || 0 })}
              className="w-full py-2 px-3 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none" />
          </div>
        </div>
      </div>
      <div className="text-right pt-1">
        <p className="text-xs text-slate-400 mb-1">Subtotal</p>
        <p className="text-sm font-semibold text-slate-800">{formatCurrency(item.quantity * item.price)}</p>
        <button type="button" onClick={() => onRemove(index)} className="mt-2 p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors">
          <Trash2 size={14} />
        </button>
      </div>
    </div>
  );
}

export default function StaffOrderCreatePage() {
  const { t } = useLanguage();
  const { businessType, store } = useAuth();
  const isGenericBusiness = businessType === 'lainnya';
  const navigate = useNavigate();
  const [form, setForm] = useState({ customer_id: '', customer_name: '', customer_phone: '', customer_whatsapp_opt_out: false, whatsapp_notifications_enabled: true, parfum: '', shelf_location: '', notes: '', dp_amount: 0 });
  const [items, setItems] = useState([{ product_id: '', product_name: '', unit: 'kg', quantity: 1, price: 0 }]);
  const [customerSearch, setCustomerSearch] = useState('');
  const [showCustDrop, setShowCustDrop] = useState(false);

  const { data: products = [] } = useQuery({ queryKey: ['products'], queryFn: () => productsApi.list().then(r => r.data) });
  const { data: custData } = useQuery({ queryKey: ['customers-search', customerSearch], queryFn: () => customersApi.list({ search: customerSearch, limit: 5 }).then(r => r.data), enabled: customerSearch.length > 1 });

  const total = items.reduce((s, i) => s + (i.quantity * i.price), 0);
  const dp = Math.min(form.dp_amount, total);

  const mutation = useMutation({
    mutationFn: () => ordersApi.create({
      ...form,
      dp_amount: dp,
      items: items.map(i => ({ product_id: i.product_id, product_name: i.product_name || 'Item', unit: i.unit, quantity: i.quantity, price: i.price }))
    }),
    onSuccess: (res) => {
      toast.success(t('Pesanan berhasil dibuat!', 'Order created!'));
      if (store?.is_demo_store && form.whatsapp_notifications_enabled) toast.info(DEMO_WA_SECURITY_MESSAGE);
      navigate(`/karyawan/orders/${res.data.id}`);
    },
    onError: err => toast.error(formatApiError(err)),
  });

  const updateItem = (i, changes) => setItems(arr => arr.map((item, idx) => idx === i ? { ...item, ...changes } : item));
  const removeItem = (i) => setItems(arr => arr.filter((_, idx) => idx !== i));
  const addItem = () => setItems(arr => [...arr, { product_id: '', product_name: '', unit: 'kg', quantity: 1, price: 0 }]);

  const selectCustomer = (c) => { setForm(f => ({ ...f, customer_id: c.id, customer_name: c.name, customer_phone: c.phone, customer_whatsapp_opt_out: Boolean(c.whatsapp_opt_out), whatsapp_notifications_enabled: !c.whatsapp_opt_out })); setCustomerSearch(c.name); setShowCustDrop(false); setPhoneHint(null); };
  const [phoneHint, setPhoneHint] = React.useState(null);
  const [phoneChecking, setPhoneChecking] = React.useState(false);
  const handlePhoneBlur = async (phone) => {
    if (!phone || form.customer_id) return;
    setPhoneChecking(true);
    try {
      const res = await customersApi.lookupByPhone(phone);
      if (res.data.found) {
        const c = res.data.customer;
        setPhoneHint({ type: 'found', name: c.name });
        setForm(f => ({ ...f, customer_id: c.id, customer_name: c.name, customer_phone: c.phone, customer_whatsapp_opt_out: Boolean(c.whatsapp_opt_out), whatsapp_notifications_enabled: !c.whatsapp_opt_out }));
        setCustomerSearch(c.name);
      } else {
        setPhoneHint({ type: 'new' });
      }
    } catch {}
    setPhoneChecking(false);
  };
  const handleSubmit = (e) => { e.preventDefault(); if (!items.some(i => i.price > 0)) { toast.error('Tambahkan minimal 1 item'); return; } mutation.mutate(); };

  return (
    <StaffLayout>
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <button onClick={() => navigate(-1)} className="p-2 rounded-xl hover:bg-slate-100 transition-colors">
            <ArrowLeft size={20} className="text-slate-600" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-slate-900 font-manrope">{t('Buat Pesanan Baru', 'New Order')}</h1>
            <p className="text-slate-500 text-sm mt-0.5">{isGenericBusiness ? t('Isi detail pesanan layanan', 'Fill in service order details') : t('Isi detail pesanan laundry', 'Fill in laundry order details')}</p>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-4">
              <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm">
                <h3 className="font-semibold text-slate-800 mb-4 font-manrope">{t('Pelanggan', 'Customer')}</h3>
                <div className="relative">
                  <input value={customerSearch} onChange={e => { setCustomerSearch(e.target.value); setShowCustDrop(true); setForm(f => ({ ...f, customer_id: '', customer_name: e.target.value })); }}
                    onFocus={() => setShowCustDrop(true)}
                    placeholder={t('Ketik nama pelanggan...', 'Type customer name...')}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-[#0f6c44]" />
                  {showCustDrop && custData?.items?.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-white rounded-xl shadow-lg border border-slate-100 overflow-hidden">
                      {custData.items.map(c => (
                        <button key={c.id} type="button" onClick={() => selectCustomer(c)}
                          className="w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-50 text-left transition-colors">
                          <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0">
                            <span className="text-[#0f6c44] text-xs font-bold">{c.name.charAt(0)}</span>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-slate-800">{c.name}</p>
                            <p className="text-xs text-slate-400">{c.phone}</p>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                {!form.customer_id && customerSearch && (
                  <div className="mt-2 space-y-1">
                    <div className="relative">
                      <input value={form.customer_phone} onChange={e => { setForm(f => ({ ...f, customer_phone: e.target.value })); setPhoneHint(null); }}
                        onBlur={e => handlePhoneBlur(e.target.value)}
                        placeholder={t('No. WhatsApp (wajib)', 'WhatsApp No. (required) *')}
                        required
                        className={`w-full px-4 py-3 bg-slate-50 border rounded-xl text-sm focus:outline-none ${!form.customer_phone ? 'border-orange-300' : 'border-slate-200 focus:border-[#0f6c44]'}`} />
                      {phoneChecking && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400">Memeriksa...</span>}
                    </div>
                    {phoneHint?.type === 'found' && (
                      <p className="text-xs text-amber-600 bg-amber-50 px-3 py-1.5 rounded-lg">Nomor WA ini sudah terdaftar sebagai <strong>{phoneHint.name}</strong> — menggunakan data tersebut.</p>
                    )}
                    {phoneHint?.type === 'new' && (
                      <p className="text-xs text-green-600 bg-green-50 px-3 py-1.5 rounded-lg">{t('Pelanggan baru akan otomatis disimpan', 'New customer will be saved automatically')}</p>
                    )}
                  </div>
                )}
                <div className="mt-4 border-t border-slate-100 pt-4">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input type="checkbox" checked={form.whatsapp_notifications_enabled} disabled={form.customer_whatsapp_opt_out} onChange={event => setForm(current => ({ ...current, whatsapp_notifications_enabled: event.target.checked }))} data-testid="staff-order-whatsapp-notifications-toggle" className="mt-0.5 h-4 w-4 accent-[#0f6c44] disabled:cursor-not-allowed" />
                    <span><span className="block text-sm font-semibold text-slate-700">Kirim notifikasi WhatsApp untuk pesanan ini</span><span className="mt-0.5 block text-xs text-slate-400">{form.customer_whatsapp_opt_out ? 'Pelanggan menonaktifkan notifikasi WhatsApp pada profilnya.' : 'Status pesanan dan konfirmasi pembayaran akan dikirim otomatis.'}</span></span>
                  </label>
                </div>
              </div>

              <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-slate-800 font-manrope">{isGenericBusiness ? t('Item Layanan', 'Service Items') : t('Item Laundry', 'Laundry Items')}</h3>
                  <button type="button" onClick={addItem}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-50 text-[#0f6c44] rounded-lg text-sm font-medium hover:bg-emerald-100 transition-colors">
                    <Plus size={16} />{t('Tambah Item', 'Add Item')}
                  </button>
                </div>
                <div className="space-y-3">
                  {items.map((item, i) => (
                    <ItemRow key={i} item={item} index={i} products={products} onUpdate={updateItem} onRemove={removeItem} />
                  ))}
                </div>
              </div>

              <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm">
                <h3 className="font-semibold text-slate-800 mb-4 font-manrope">{t('Detail Tambahan', 'Additional Details')}</h3>
                <div className="grid grid-cols-2 gap-3">
                  {!isGenericBusiness && <div>
                    <label className="block text-sm font-medium text-slate-600 mb-1.5">{t('Parfum', 'Fragrance')}</label>
                    <select value={form.parfum} onChange={e => setForm(f => ({ ...f, parfum: e.target.value }))}
                      className="w-full py-2.5 px-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none">
                      <option value="">- {t('Tanpa parfum', 'No fragrance')} -</option>
                      {PARFUMS.map(p => <option key={p} value={p}>{p}</option>)}
                    </select>
                  </div>}
                  {!isGenericBusiness && <div>
                    <label className="block text-sm font-medium text-slate-600 mb-1.5">{t('Lokasi Rak', 'Shelf')}</label>
                    <input value={form.shelf_location} onChange={e => setForm(f => ({ ...f, shelf_location: e.target.value }))}
                      placeholder="Rak A-1"
                      className="w-full py-2.5 px-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none" />
                  </div>}
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-slate-600 mb-1.5">{t('Catatan', 'Notes')}</label>
                    <input value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                      placeholder={t('Catatan khusus...', 'Special notes...')}
                      className="w-full py-2.5 px-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none" />
                  </div>
                </div>
              </div>
            </div>

            <div className="lg:col-span-1">
              <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm sticky top-6">
                <h3 className="font-semibold text-slate-800 mb-4 font-manrope">{t('Ringkasan', 'Summary')}</h3>
                <div className="space-y-2 mb-4">
                  {items.filter(i => i.price > 0).map((item, i) => (
                    <div key={i} className="flex justify-between text-sm">
                      <span className="text-slate-600 truncate mr-2">{item.product_name || `Item ${i+1}`} ({item.quantity} {item.unit})</span>
                      <span className="text-slate-800 font-medium whitespace-nowrap">{formatCurrency(item.quantity * item.price)}</span>
                    </div>
                  ))}
                </div>
                <div className="border-t border-slate-100 pt-3 mb-4">
                  <div className="flex justify-between font-bold mb-3">
                    <span>Total</span>
                    <span className="text-[#0f6c44] font-manrope">{formatCurrency(total)}</span>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-600 mb-1.5">DP</label>
                    <input type="number" value={form.dp_amount} min={0} max={total}
                      onChange={e => setForm(f => ({ ...f, dp_amount: parseFloat(e.target.value) || 0 }))}
                      className="w-full py-2.5 px-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none" />
                  </div>
                  <div className="mt-3 flex justify-between text-sm">
                    <span className="text-slate-500">{t('Sisa', 'Remaining')}</span>
                    <span className={`font-semibold ${(total - dp) > 0 ? 'text-orange-600' : 'text-green-600'}`}>{formatCurrency(total - dp)}</span>
                  </div>
                </div>
                <button type="submit" disabled={mutation.isPending || total === 0}
                  className="w-full h-12 bg-[#0f6c44] text-white rounded-xl font-semibold hover:bg-[#0c5636] active:scale-95 transition-all flex items-center justify-center gap-2 disabled:opacity-70">
                  {mutation.isPending ? <Spinner size="sm" className="border-white/30 border-t-white" /> : t('Buat Pesanan', 'Create Order')}
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>
    </StaffLayout>
  );
}
